// Trade-slug helpers for the /lanes/trade/<slug> hub pages. Kept in sync with prerender.trades.mjs
// (same slug rules, same ≥5-lane bar, Oceania special-cased to "pacific-islands" / "Pacific Islands").
import type { QA } from '../components/Faq'
import { LANES, type Lane } from './lanes'

export const MIN_TRADE_LANES = 5

// Per-trade FAQ, templated from the trade title + lane count. Kept byte-identical to tradeFaq() in
// prerender.trades.mjs (a build-time check in prerender.mjs asserts this) so the visible answer, the
// hydrated app, and the FAQPage JSON-LD never drift. Methodology only. No fabricated figures.
export function tradeFaq(title: string, laneCount: number): QA[] {
  return [
    {
      q: `Which carriers sail ${title} lanes?`,
      a: `SchedulesMCP tracks the carrier line-up on each of the ${laneCount} ${title} lanes it covers. Open any lane in the table to see its carriers, or use the Compare tool to rank every carrier on a specific ${title} route by transit time and observed on-time reliability.`,
    },
    {
      q: `How do I compare transit times on ${title} lanes?`,
      a: `Pick a lane below to see its median port-to-port transit, or open the Compare tool and enter an origin and destination on the ${title} trade to rank carriers by observed median and p90 transit before you book.`,
    },
    {
      q: `Where do ${title} sailing schedules come from?`,
      a: `Each carrier's forward ${title} sailings come from that line's own e-service or published schedule API, normalised into one common shape so every carrier on the trade reads the same and can be compared side by side.`,
    },
  ]
}

// Slugify a trade name into a URL-safe slug. Oceania → the friendlier "pacific-islands".
export function tradeSlug(trade: string): string {
  if (trade === 'Oceania') return 'pacific-islands'
  return trade
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function tradeTitle(trade: string): string {
  return trade === 'Oceania' ? 'Pacific Islands' : trade
}

export interface TradeGroup {
  trade: string
  slug: string
  title: string
  lanes: Lane[]
}

// All trades with ≥5 lanes, lanes sorted by sailings desc. Order: by lane count desc.
export function qualifyingTrades(): TradeGroup[] {
  const byTrade = new Map<string, Lane[]>()
  for (const l of LANES) {
    const list = byTrade.get(l.trade) ?? []
    list.push(l)
    byTrade.set(l.trade, list)
  }
  return [...byTrade.entries()]
    .filter(([, lanes]) => lanes.length >= MIN_TRADE_LANES)
    .map(([trade, lanes]) => ({
      trade,
      slug: tradeSlug(trade),
      title: tradeTitle(trade),
      lanes: [...lanes].sort((a, b) => b.sailings - a.sailings),
    }))
    .sort((a, b) => b.lanes.length - a.lanes.length)
}

// Look up a single trade group by its URL slug (undefined if unknown or under the 5-lane bar).
export function tradeBySlug(slug: string): TradeGroup | undefined {
  return qualifyingTrades().find((t) => t.slug === slug)
}
